import { MediaLibraryItem } from '../../../api/media';
import { constructMediaUrl } from '../../../api/helpers';
import { resolveMediaPreviewCandidates } from '../mediaPreview';
import { inferFolderFromType, inferSourceBucket } from './helpers';
import { readGeneratedOriginLabel } from './browseTabUtils';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (s: string): boolean => UUID_RE.test(s.trim());

export const promptSnippet = (prompt: string, words = 8): string =>
  prompt.trim().split(/\s+/).slice(0, words).join(' ');

/** A human-friendly label for a media item: real name → prompt snippet → date → id. */
export const mediaLabel = (item: MediaLibraryItem): string => {
  const mediaId = item.media_id || item.id;
  if (item.name && !isUuid(item.name)) return item.name;
  if (item.prompt) return promptSnippet(item.prompt);
  if (item.created_at) {
    const when = new Date(item.created_at);
    if (!Number.isNaN(when.getTime())) {
      return `Image generated ${when.toLocaleDateString()}`;
    }
  }
  return mediaId;
};

export type MediaFolder = 'all' | 'image' | 'video' | 'audio';

export const mediaFolder = (item: MediaLibraryItem): MediaFolder =>
  inferFolderFromType(item.type, item.mime_type);

/** Does this item descend from another (a generated variation)? */
export const hasParent = (item: MediaLibraryItem): boolean => {
  const meta = item.metadata;
  if (!meta || typeof meta !== 'object') return false;
  const parent = (meta as Record<string, unknown>).parent_media_id;
  return typeof parent === 'string' && parent.trim().length > 0;
};

export interface ThumbSources {
  folder: MediaFolder;
  /** Best static thumbnail (webp derivative preferred) resolved to a URL. */
  thumbnailUrl: string;
  /** Source video url, for hover-scrub / first-frame thumbnail fallback. */
  videoUrl: string;
  /** Resolved full-resolution / playable url (used by the inspector). */
  fullUrl: string;
  audioUrl: string;
  /**
   * Tiny base64 data-URI blur-up placeholder (~28px webp), painted CSS-blurred
   * under the cell so it never flashes empty. '' when the backend has none.
   */
  microThumb: string;
}

/**
 * Resolve the candidate URLs a card needs. Prefers the backend's lightweight
 * `thumbnail_url` (webp derivative) for the grid cell; the full-res original is
 * only used in the inspector preview.
 */
export const resolveThumbSources = (item: MediaLibraryItem): ThumbSources => {
  const { folder, image, video, audio, poster } = resolveMediaPreviewCandidates(item);

  // thumbnail_url (webp) ranks first for the cell; then poster, then any image.
  const thumbCandidate = item.thumbnail_url || poster[0] || image[0] || '';
  const videoCandidate = video[0] || '';
  const fullCandidate = item.url || image[0] || video[0] || '';
  const audioCandidate = audio[0] || '';

  return {
    folder,
    thumbnailUrl: thumbCandidate ? constructMediaUrl(thumbCandidate) : '',
    videoUrl: videoCandidate ? constructMediaUrl(videoCandidate) : '',
    fullUrl: fullCandidate ? constructMediaUrl(fullCandidate) : '',
    audioUrl: audioCandidate ? constructMediaUrl(audioCandidate) : '',
    // micro_thumb is already a self-contained data-URI — used as-is, never
    // routed through constructMediaUrl (it isn't a server path).
    microThumb: item.micro_thumb || '',
  };
};

const DOWNLOAD_EXTENSION_BY_MIME: Record<string, string> = {
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/svg+xml': 'svg',
  'image/webp': 'webp',
};
const KNOWN_IMAGE_EXTENSION_RE = /\.(?:gif|jpe?g|png|svg|webp)$/i;

/** Stable, filesystem-safe filename for downloading an image original. */
export const mediaDownloadName = (item: MediaLibraryItem): string => {
  const fallback = mediaLabel(item) || item.media_id || item.id || 'image';
  const base = (item.name || fallback)
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .slice(0, 120) || 'image';
  if (KNOWN_IMAGE_EXTENSION_RE.test(base)) return base;
  const urlExtension = item.url?.match(/\.([a-z0-9]{2,5})(?:[?#]|$)/i)?.[1]?.toLowerCase();
  const mimeExtension = item.mime_type
    ? DOWNLOAD_EXTENSION_BY_MIME[item.mime_type.toLowerCase()]
    : undefined;
  return `${base}.${mimeExtension || urlExtension || 'png'}`;
};

export type MediaStatus = 'ready' | 'generating' | 'failed';

export const mediaStatus = (item: MediaLibraryItem): MediaStatus => {
  if (item.status === 'generating' || item.status === 'failed') return item.status;
  return 'ready';
};

export interface TypeBadge {
  label: string;
  className: string;
}

/** The bottom-right type badge spec for a folder. */
export const typeBadge = (folder: MediaFolder): TypeBadge => {
  switch (folder) {
    case 'video':
      return { label: '▶ VID', className: 'media-badge-video' };
    case 'audio':
      return { label: '♪ AUD', className: 'media-badge-audio' };
    case 'image':
      return { label: 'IMG', className: 'media-badge-image' };
    default:
      return { label: 'FILE', className: 'media-badge-image' };
  }
};

/** Short origin label for generated items (checkpoint/run), '' for uploads. */
export const originLabel = (item: MediaLibraryItem): string =>
  inferSourceBucket(item.source, item.metadata) === 'generated'
    ? readGeneratedOriginLabel(item)
    : '';

// ── Sorting ────────────────────────────────────────────────────────────────

export type SortKey = 'newest' | 'oldest' | 'favourite' | 'name';

export const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'favourite', label: 'Favourited' },
  { value: 'name', label: 'Name A–Z' },
];

const createdMs = (item: MediaLibraryItem): number => {
  if (!item.created_at) return 0;
  const t = new Date(item.created_at).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Sort a list of items by the chosen key. Pure + returns a new array so callers
 * can memoize on (items, sortKey). 'favourite' floats favourites first, newest
 * within each bucket — a "recently favourited" approximation without a separate
 * favourited_at field.
 */
export const sortMediaItems = (items: MediaLibraryItem[], key: SortKey): MediaLibraryItem[] => {
  const next = [...items];
  switch (key) {
    case 'oldest':
      return next.sort((a, b) => createdMs(a) - createdMs(b));
    case 'name':
      return next.sort((a, b) => mediaLabel(a).localeCompare(mediaLabel(b)));
    case 'favourite':
      return next.sort((a, b) => {
        const fa = a.is_favourite ? 1 : 0;
        const fb = b.is_favourite ? 1 : 0;
        if (fa !== fb) return fb - fa;
        return createdMs(b) - createdMs(a);
      });
    case 'newest':
    default:
      return next.sort((a, b) => createdMs(b) - createdMs(a));
  }
};

// ── Density ──────────────────────────────────────────────────────────────────

export type Density = 'comfortable' | 'cozy' | 'compact';

export const DENSITY_CELL: Record<Density, number> = {
  comfortable: 240,
  cozy: 180,
  compact: 120,
};

const DENSITY_KEY = 'contentgen.media.density';

export const readPersistedDensity = (): Density => {
  try {
    const v = window.localStorage.getItem(DENSITY_KEY);
    if (v === 'comfortable' || v === 'cozy' || v === 'compact') return v;
  } catch {
    // localStorage may be unavailable (private mode / SSR) — fall through.
  }
  return 'cozy';
};

export const persistDensity = (density: Density): void => {
  try {
    window.localStorage.setItem(DENSITY_KEY, density);
  } catch {
    // Ignore quota / availability errors.
  }
};
